// All landing-page copy lives here rather than in the i18n bundles: the marketing
// page is Arabic-only (same as the rest of the client app, which mounts RTL), and
// keeping it out of ar.json/en.json avoids polluting the in-app translation
// namespaces that the authenticated screens share.

export const SIGN_IN_PATH = '/clients/sign_in';

// Production host from the client ingress (shared-devops/manifests/eldoctor/
// eldoctor-client.yml). Used for canonical/og:url, which must be absolute and
// must point at the live domain regardless of the environment rendering it.
export const SITE_URL = 'https://eldoctor-co.com';

export const BRAND = {
  legalName: 'الصفوة للتجارة والاستيراد',
  legalNameLatin: 'El Safwa Trading and Import',
  knownAs: 'الدكتور',
  latin: 'EL DOCTOR',
  tagline: 'قطع غيار السيارات',
  since: '1984',
};

// Opening an account starts with a phone call, so the number is a primary CTA
// rather than a footer detail. `tel` is the dialable form, `phone` the one
// people read.
//
// `phone` is wrapped in U+2066 LEFT-TO-RIGHT ISOLATE / U+2069 POP DIRECTIONAL
// ISOLATE. Without them the bidi algorithm reorders the "+" and the digit groups
// when the number sits inside Arabic text — "⁦+20 2 22698676⁩" came out as
// "22698676 2 20+". The isolates are invisible and survive string interpolation,
// which plain `dir="ltr"` markup cannot do inside these copy strings.
export const CONTACT = {
  phone: '⁦+20 2 22698676⁩',
  tel: '+20222698676',
  hoursShort: '10 ص – 5 م · الأحد إجازة',
  hoursLong: 'من 10 صباحًا حتى 5 مساءً — يوميًا ما عدا يوم الأحد',
  // schema.org day names for the openingHoursSpecification below.
  openDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  opens: '10:00',
  closes: '17:00',
};

export const SEO = {
  title: 'الدكتور | El Doctor لقطع غيار السيارات — هيونداي وكيا وشيفروليه',
  description:
    'الدكتور (Eldoctor) — الصفوة للتجارة والاستيراد، توريد قطع غيار السيارات للمحلات والورش وتجار الجملة منذ 1984. كتالوج بالماركة والموديل والفئة، بحث بالكود، كشوف أسعار، وطلب أونلاين في أي وقت.',
  // Kept short and honest; these are the terms the business is actually known by.
  keywords:
    'الدكتور, El Doctor, Eldoctor, قطع غيار سيارات, قطع غيار هيونداي, قطع غيار كيا, قطع غيار شيفروليه, الصفوة للتجارة والاستيراد, El Safwa Trading and Import, El Doctor Auto Parts Supply',
};

export const NAV_LINKS = [
  { id: 'why', label: 'ليه الدكتور' },
  { id: 'how', label: 'إزاي تطلب' },
  { id: 'makers', label: 'الماركات' },
  { id: 'brands', label: 'ماركات القطع' },
  { id: 'about', label: 'من نحن' },
  { id: 'faq', label: 'أسئلة شائعة' },
];

// The header shows a subset — six links plus a sign-in button crowds the bar.
// The full list still appears in the mobile drawer and the footer.
export const HEADER_LINKS = NAV_LINKS.filter((link) => !['brands', 'faq'].includes(link.id));

export const HERO_POINTS = ['في السوق من سنة 1984', 'حساب خاص لكل عميل', 'طلب أونلاين في أي وقت'];

export const FEATURES = [
  {
    icon: 'search',
    title: 'بحث سريع',
    body: 'اختار الماركة والموديل والفئة، وتوصل للقطعة اللي محتاجها في ثوانٍ.',
  },
  {
    icon: 'code',
    title: 'بحث بالكود',
    body: 'عارف كود الصنف؟ اكتبه وهو يوصلك للقطعة على طول من غير لف ودوران.',
  },
  {
    icon: 'photo',
    title: 'صور واضحة للقطعة',
    body: 'شوف الصنف قبل ما تطلبه، وقارن بينه وبين البدائل المتاحة في نفس الموديل.',
  },
  {
    icon: 'document',
    title: 'كشف أسعار جاهز',
    body: 'استخرج كشف أسعار بالموديلات والفئات اللي تختارها، جاهز للطباعة والمشاركة.',
  },
  {
    icon: 'wallet',
    title: 'حسابك تحت السيطرة',
    body: 'رصيدك وحركاتك وطلباتك السابقة كلها قدامك في مكان واحد، أول بأول.',
  },
  {
    icon: 'bolt',
    title: 'توفّر لحظي',
    body: 'الكميات المتاحة بتتحدّث لحظة بلحظة، فبتطلب وانت مطمّن إن القطعة موجودة.',
  },
];

export const STEPS = [
  {
    icon: 'phone',
    title: 'اتصل بينا الأول',
    body: 'كلّمنا على ⁦+20 2 22698676⁩ ونتفق على التعامل ونفتح لك حساب عميل خاص بيك.',
  },
  {
    icon: 'user',
    title: 'سجّل دخولك',
    body: 'ادخل ببيانات حسابك، وهتلاقي أسعارك وعروضك انت بالذات.',
  },
  {
    icon: 'search',
    title: 'دوّر واملا السلة',
    body: 'بالماركة والموديل والفئة أو بكود الصنف، وضيف الكميات اللي محتاجها.',
  },
  {
    icon: 'send',
    title: 'ابعت الطلبية',
    body: 'ابعتها لمندوبك بضغطة واحدة، وتابع حالتها من صفحة طلباتك.',
  },
];

export const AUDIENCE = [
  {
    icon: 'truck',
    title: 'محلات قطع الغيار',
    body: 'اطلب اللي ناقصك أول بأول من غير مكالمات ولا كشوف ورق، وشوف المتاح فعلاً قبل ما توعد زبونك.',
  },
  {
    icon: 'shield',
    title: 'الورش ومراكز الصيانة',
    body: 'وصّل للقطعة الصح للموديل الصح من أول مرة — بحث بالموديل وصور واضحة وبدائل مذكورة.',
  },
  {
    icon: 'globe',
    title: 'تجار الجملة',
    body: 'كشوف أسعار جاهزة بالنسبة اللي تناسبك، وسجل كامل لكل عملية على حسابك.',
  },
];

export const ORIGINS = ['كوريا', 'اليابان', 'ألمانيا', 'أوروبا', 'تايوان', 'الصين', 'الهند'];

export const ABOUT_POINTS = [
  'خبرة في سوق قطع الغيار من سنة 1984، وعلاقات تعامل بنيناها على مدى السنين دي.',
  'تخصّص عميق في سيارات الماركات الكورية والآسيوية بدل ما نبيع كل حاجة ونتقن مفيش.',
  'أصناف أصلية وبدائل معتمدة من ماركات عالمية معروفة، بمنشأ محدّد لكل صنف.',
  'كتالوج مترتّب بالماركة والموديل والفئة، بنفس اللغة اللي بتتكلمها الورشة.',
  'منصة أونلاين بدل التليفون والورق، شغالة طول الوقت من أي جهاز.',
];

export const FAQ = [
  {
    q: 'إزاي أبدأ التعامل وأفتح حساب على المنصة؟',
    a: 'التعامل بيبدأ بمكالمة. اتصل بينا على ⁦+20 2 22698676⁩ خلال مواعيد العمل — من 10 صباحًا حتى 5 مساءً يوميًا ما عدا يوم الأحد — ونتفق على تفاصيل التعامل ونفتح لك حساب عميل. بعد تفعيل الحساب هتستلم بيانات الدخول وتقدر تبدأ الطلب على طول.',
  },
  {
    q: 'مواعيد العمل إيه؟',
    a: 'بنشتغل من 10 صباحًا حتى 5 مساءً يوميًا، ويوم الأحد إجازة. المنصة نفسها شغالة 24 ساعة وتقدر تبعت طلبك في أي وقت، والمندوب هيتابع معاك أول ما نفتح.',
  },
  {
    q: 'نسيت بيانات الدخول، أعمل إيه؟',
    a: 'اتصل بينا على ⁦+20 2 22698676⁩ أو كلّم مندوبك وهيتم إعادة ضبط بيانات حسابك. عشان أمان حسابك، بيانات الدخول بتتسلّم للعميل نفسه بس.',
  },
  {
    q: 'هل كل العملاء بيشوفوا نفس الأسعار؟',
    a: 'لأ. كل عميل بيشوف الأسعار والعروض والكشوف الخاصة بحسابه هو، ومفيش أي بيانات بتتشارك بين الحسابات.',
  },
  {
    q: 'أقدر أستخدم المنصة من الموبايل؟',
    a: 'أيوه. المنصة شغالة من الموبايل والتابلت والكمبيوتر بنفس الإمكانيات، من غير ما تحتاج تنزّل أي تطبيق.',
  },
  {
    q: 'إزاي أتابع طلباتي ورصيدي؟',
    a: 'من صفحة «طلباتك القديمة» هتلاقي كل طلبية بعتها، ومن صفحة «حسابك» هتلاقي رصيدك وحركات الحساب أول بأول.',
  },
];

export const FOOTER_LINKS = NAV_LINKS;
